import { css } from '@firebolt-dev/css'
import { useEffect, useRef, useState } from 'react'

import { InterfaceView } from './InterfaceView'
import { isTouch } from '../utils'

export function GameUI({ world }) {
  const [inventory, setInventory] = useState({ type: 'equipment', slots: [], held: null })
  const [interact, setInteract] = useState({ type: null })
  const [currentInterface, setCurrentInterface] = useState(null)
  const [equipment, setEquipment] = useState(false)
  const [chat, setChat] = useState(false)
  const [activeSlot, setActiveSlot] = useState(null)
  const [muted, setMuted] = useState(false)
  const [resonance, setResonance] = useState(100)

  const moveRef = useRef()
  const aimRef = useRef()

  useEffect(() => {
    if (!isTouch) return
    let raf
    function update() {
      const ctrl = world.controller
      const input = world.input
      const moveEl = moveRef.current
      const aimEl = aimRef.current
      const w = window.innerWidth
      const h = window.innerHeight
      if (moveEl) {
        const stick = ctrl._stick
        const touch = stick ? input.touches.get(stick.id) : null
        if (touch) {
          moveEl.style.left = touch.position.x + 'px'
          moveEl.style.top = touch.position.y + 'px'
        } else {
          moveEl.style.left = '100px'
          moveEl.style.top = h - 140 + 'px'
        }
      }
      if (aimEl) {
        const stick = ctrl._aimStick
        const touch = stick ? input.touches.get(stick.id) : null
        if (touch) {
          aimEl.style.left = touch.position.x + 'px'
          aimEl.style.top = touch.position.y + 'px'
        } else {
          aimEl.style.left = w - 100 + 'px'
          aimEl.style.top = h - 140 + 'px'
        }
      }
      raf = requestAnimationFrame(update)
    }
    raf = requestAnimationFrame(update)
    return () => cancelAnimationFrame(raf)
  }, [])

  const mounted = useRef(false)
  const prevUI = useRef({ equipment: false, chat: false, interfaceOpen: false })
  useEffect(() => {
    const interfaceOpen = !!currentInterface
    if (!mounted.current) {
      mounted.current = true
      prevUI.current = { equipment, chat, interfaceOpen }
      return
    }
    const prev = prevUI.current
    const changed = equipment !== prev.equipment || chat !== prev.chat || interfaceOpen !== prev.interfaceOpen
    prevUI.current = { equipment, chat, interfaceOpen }
    if (!changed) return
    const { x, y } = world.camera.position
    world.audio.play({ name: 'interface', pitch: 0.05, volume: 0.5 }, x, y)
  }, [equipment, chat, currentInterface])

  const api = useRef({})
  api.current.use = () => {
    if (currentInterface) return world.controller.closeInterface()
    if (!equipment) world.controller.performUse()
  }
  api.current.close = () => {
    if (equipment) setEquipment(false)
    if (chat) setChat(false)
    if (currentInterface) world.controller.closeInterface()
  }
  api.current.chatOpen = chat
  api.current.openChat = () => {
    setCurrentInterface(null)
    setEquipment(false)
    setChat(true)
  }

  useEffect(() => {
    function onInventory(data) {
      setInventory({ ...data })
    }
    function onInteract(interact) {
      setInteract(interact)
    }
    function onInterface(nextInterface) {
      setCurrentInterface(nextInterface)
    }
    function onKeyDown(e) {
      // todo: these should be in-engine, eg Client.js
      if (e.code === 'Tab' && document.activeElement.tagName !== 'INPUT') {
        e.preventDefault()
        if (world.entities.get(world.playerId)?.data.admin) world.emit('toggleEditor')
        return
      }
      if (e.code === 'KeyB') setEquipment(value => !value)
      if (e.code === 'Escape') api.current.close()
      if ((e.code === 'Enter' || e.code === 'Slash') && document.activeElement.tagName !== 'INPUT') {
        if (api.current.chatOpen) {
          document.querySelector('.chat-input')?.focus()
        } else {
          api.current.openChat()
        }
      }
    }
    if (world.inventory.data) {
      onInventory(world.inventory.data)
    }
    world.on('inventory', onInventory)
    world.on('interact', onInteract)
    world.on('interface', onInterface)
    world.on('hotbarHighlight', setActiveSlot)
    world.on('resonance', setResonance)
    window.addEventListener('keydown', onKeyDown)
    return () => {
      world.off('inventory', onInventory)
      world.off('interact', onInteract)
      world.off('interface', onInterface)
      world.off('hotbarHighlight', setActiveSlot)
      world.off('resonance', setResonance)
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [])

  // chest/container opens → close bag and chat
  useEffect(() => {
    if (currentInterface) {
      setEquipment(false)
      setChat(false)
    }
  }, [currentInterface])

  // bag opens → close any open container
  useEffect(() => {
    if (equipment) world.controller.closeInterface()
  }, [equipment])

  return (
    <div
      className='ui'
      css={css`
        position: absolute;
        inset: 0;
        z-index: 10;
        pointer-events: none;

        .ui-btns {
          position: absolute;
          top: calc(env(safe-area-inset-top) + 15px);
          right: calc(env(safe-area-inset-right) + 15px);
          display: flex;
          align-items: center;
        }
        .ui-btn {
          width: 36px;
          height: 36px;
          background-size: cover;
          image-rendering: pixelated;
          display: flex;
          align-items: center;
          justify-content: center;
          pointer-events: auto;
          &:hover {
            transform: scale(1.1);
          }
        }

        /* Interface anchor — defines the zone the interface fits into.
           Set via width+centering for a width-capped zone, or top/bottom/left/right
           for a fully bounded zone. Interface fills bottom-center within it.
           All responsive layout lives here. */
        .interface-anchor {
          position: absolute;
          top: 15px;
          bottom: 15px;
          left: 50%;
          transform: translateX(-50%);
          width: calc(100% - 30px);
          max-width: 400px;
        }
        .ui-joystick {
          pointer-events: none;
          position: fixed;
          width: 96px;
          height: 96px;
          background-size: cover;
          image-rendering: pixelated;
          transform: translate(-50%, -50%);
        }
        .ui-joystick-move {
          background-image: url('/assets/btn-move.png');
        }
        .ui-joystick-aim {
          background-image: url('/assets/btn-aim.png');
        }
        .ui-interact {
          pointer-events: auto;
          position: absolute;
          width: 54px;
          height: 54px;
          bottom: 180px;
          right: 120px;
          background-image: url('/assets/interact.png');
          background-size: cover;
          image-rendering: pixelated;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        @media (max-width: 650px) {
          .ui-interact {
            bottom: 180px;
            right: 120px;
          }
        }
      `}
    >
      {isTouch && <div ref={moveRef} className='ui-joystick ui-joystick-move' />}
      {isTouch && <div ref={aimRef} className='ui-joystick ui-joystick-aim' />}
      {!currentInterface && !equipment && isTouch && interact?.type && (
        <div
          className='ui-interact pointer'
          onPointerDown={e => {
            e.stopPropagation()
            e.preventDefault()
            world.controller.performInteract()
          }}
        >
          {/* <span>{interact.label}</span> */}
        </div>
      )}
      <div className='ui-btns'>
        <div
          className='ui-btn pointer'
          css={css`
            background: ${muted ? 'url(/assets/audio-muted.png)' : 'url(/assets/audio-unmuted.png)'};
          `}
          onPointerDown={e => {
            e.stopPropagation()
            world.audio.toggleMute()
            setMuted(m => !m)
          }}
        />
      </div>
      {!currentInterface && !equipment && !chat && (
        <div className='interface-anchor'>
          <InterfaceView
            kind='hotbar'
            name={null}
            localInv={inventory}
            targetInv={null}
            activeSlot={activeSlot}
            resonance={resonance}
            onMove={data => world.controller.moveInterface(data)}
            onSelect={idx => world.controller.activateHotbarSlot(idx)}
            onGoEquipment={() => setEquipment(true)}
            onGoChat={() => setChat(true)}
          />
        </div>
      )}
      {!currentInterface && !equipment && chat && (
        <div className='interface-anchor'>
          <InterfaceView
            world={world}
            kind='chat'
            name={null}
            localInv={inventory}
            targetInv={null}
            activeSlot={activeSlot}
            onMove={data => world.controller.moveInterface(data)}
            onSelect={idx => world.controller.activateHotbarSlot(idx)}
            onGoHotbar={() => setChat(false)}
            onGoEquipment={() => setEquipment(true)}
            onCloseChat={() => setChat(false)}
          />
        </div>
      )}
      {!currentInterface && equipment && (
        <div className='interface-anchor'>
          <InterfaceView
            kind='equipment'
            name={null}
            localInv={inventory}
            targetInv={null}
            onMove={data => world.controller.moveInterface(data)}
            onGoHotbar={() => setEquipment(false)}
            onGoChat={() => {
              setEquipment(false)
              setChat(true)
            }}
          />
        </div>
      )}
      {currentInterface && !equipment && (
        <div className='interface-anchor'>
          <InterfaceView
            kind={currentInterface.kind}
            name={currentInterface.name}
            localInv={inventory}
            targetInv={currentInterface.inventory}
            recipes={currentInterface.recipes}
            onMove={data => world.controller.moveInterface(data)}
            onTakeAll={() => world.controller.takeAllInterface()}
            onCraft={recipeId => world.controller.craftItem(recipeId)}
            onGoHotbar={() => world.controller.closeInterface()}
            onGoChat={() => {
              world.controller.closeInterface()
              setChat(true)
            }}
          />
        </div>
      )}
    </div>
  )
}
